import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ConnectedAccountDto, ConnectedAccountsApiService } from './api/connected-accounts.api.service';
import { AuthService } from './auth.service';
import { SnackbarService } from './snackbar.service';

/**
 * Serwis zarządzania powiązanymi kontami użytkownika.
 *
 * - Cache listy w BehaviorSubject (auto-refresh po attach/unlink/switch).
 * - Switch deleguje do AuthService.applyLoginData (wspólna ścieżka jak login).
 * - Każde konto ma osobną bazę Dexie — switch NIE usuwa lokalnej bazy poprzedniego konta.
 */
@Injectable({ providedIn: 'root' })
export class ConnectedAccountsService {

    private readonly _accounts$ = new BehaviorSubject<ConnectedAccountDto[]>([]);
    public readonly accounts$ = this._accounts$.asObservable();

    constructor(
        private _api: ConnectedAccountsApiService,
        private _authService: AuthService,
        private _snackbar: SnackbarService,
        private _deviceService: DeviceDetectorService
    ) { }

    public get accounts(): ConnectedAccountDto[] {
        return this._accounts$.value;
    }

    /**
     * Pobiera listę powiązanych kont z backendu i aktualizuje cache.
     */
    public loadList(): Observable<ConnectedAccountDto[]> {
        return new Observable<ConnectedAccountDto[]>(subscriber => {
            this._api.list().subscribe({
                next: (response) => {
                    if (response?.success) {
                        const list = response.data || [];
                        this._accounts$.next(list);
                        subscriber.next(list);
                    } else {
                        this._accounts$.next([]);
                        subscriber.next([]);
                    }
                    subscriber.complete();
                },
                error: () => {
                    this._accounts$.next([]);
                    subscriber.next([]);
                    subscriber.complete();
                }
            });
        });
    }

    /**
     * Czyści cache (np. po wylogowaniu).
     */
    public clear(): void {
        this._accounts$.next([]);
    }

    /**
     * Zasila cache listą otrzymaną z innego źródła (np. /account/settings przy loginie/switchu).
     * Dzięki temu lista w UI jest świeża zaraz po zalogowaniu, bez dodatkowego HTTP.
     */
    public setAccounts(accounts: ConnectedAccountDto[] | null | undefined): void {
        this._accounts$.next(Array.isArray(accounts) ? accounts : []);
    }

    /**
     * Powiązanie nowego konta. Jeśli wymaga 2FA — zwraca tempToken w response.data.
     */
    public attach(emailAddress: string, password: string): Observable<any> {
        return this._api.attach(emailAddress, password).pipe(
            tap(response => {
                if (response?.success && !response?.data?.requiresTwoFactor) {
                    this.loadList().subscribe();
                }
            })
        );
    }

    /**
     * Dokańcza powiązanie po podaniu kodu 2FA.
     */
    public verifyAttach2fa(tempToken: string, code: string): Observable<any> {
        return this._api.verifyAttach2fa(tempToken, code).pipe(
            tap(response => {
                if (response?.success) {
                    this.loadList().subscribe();
                }
            })
        );
    }

    /**
     * Odpina konto (dwustronnie).
     */
    public unlink(linkId: string): Observable<any> {
        return this._api.unlink(linkId).pipe(
            tap(response => {
                if (response?.success) {
                    this.loadList().subscribe();
                }
            })
        );
    }

    /**
     * Przełącza na powiązane konto. Po sukcesie:
     * - zapisuje nowe tokeny w localStorage,
     * - przełącza Dexie na bazę nowego usera (poprzednia zostaje),
     * - synchronizuje dane nowego konta,
     * - przekierowuje na /konto.
     */
    public switchTo(account: ConnectedAccountDto): Observable<any> {
        const deviceMeta = this._buildDeviceMeta();
        return new Observable<any>(subscriber => {
            this._api.switchTo(account._id, deviceMeta).subscribe({
                next: (response) => {
                    if (response?.success && response.data) {
                        this._authService.applyLoginData(
                            { ...response.data, email: account.email },
                            { redirect: true, mode: 'switch' }
                        );
                    } else {
                        this._snackbar.showSnackBar(
                            response?.message || 'Nie udało się przełączyć konta',
                            'warning'
                        );
                    }
                    subscriber.next(response);
                    subscriber.complete();
                },
                error: (err) => {
                    this._snackbar.showSnackBar('Nie udało się przełączyć konta', 'error');
                    subscriber.error(err);
                }
            });
        });
    }

    private _buildDeviceMeta(): { browser?: string; deviceName?: string; platform?: string } {
        try {
            const info = this._deviceService.getDeviceInfo();
            return {
                browser: `${info.browser} v${info.browser_version}`,
                deviceName: `${info.os} ${info.os_version}`,
                platform: this._deviceService.deviceType
            };
        } catch {
            return { platform: 'web' };
        }
    }
}
